import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  useColorMode,
  Spacer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  TableContainer,
  IconButton,
  Select,
  Input,
  Flex,
  Button,
  HStack,
  VStack,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  MenuGroup,
  MenuOptionGroup,
  MenuDivider,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import SettingsMenu from "../common/SettingsMenu";
import axiosInstance from "../../helpers/axiosInstance";

import { ReactComponent as DownloadIcon } from "../../assets/icons/download_2_fill_icon.svg";

const Newsletter = () => {
  const { colorMode } = useColorMode();
  const toast = useToast();
  const [emails, setEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [totalEmails, setTotalEmails] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const entriesPerPage = 30;

  // Filter states
  const [filters, setFilters] = useState({
    emailSearch: "",
    signupType: "all",
    dateSort: "newest",
  });
  
  // Email search states
  const [emailSearchInput, setEmailSearchInput] = useState("");
  const [filteredEmailsForDropdown, setFilteredEmailsForDropdown] = useState([]);

  // Fetch all subscribed emails with pagination
  const fetchEmails = async (page = 1) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/newsletter/?page=${page}`);
      if (response.data) {
        setEmails(response.data.emails);
        setFilteredEmails(response.data.emails);
        setTotalEmails(response.data.total);
        setTotalPages(response.data.total_pages);
        setCurrentPage(response.data.page);
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch emails",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error fetching emails:", error);
      toast({
        title: "Error",
        description: "An error occurred while fetching emails.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch emails on component mount
  useEffect(() => {
    fetchEmails(1);
  }, []);

  // Filter emails for dropdown based on search input
  useEffect(() => {
    if (emailSearchInput) {
      const filtered = emails
        .filter((email, index, self) => 
          index === self.findIndex(e => e.email === email.email)
        )
        .filter(email => 
          email.email.toLowerCase().includes(emailSearchInput.toLowerCase())
        )
        .slice(0, 20); // Limit to 20 results for performance
      setFilteredEmailsForDropdown(filtered);
    } else {
      const uniqueEmails = emails
        .filter((email, index, self) => 
          index === self.findIndex(e => e.email === email.email)
        )
        .slice(0, 20);
      setFilteredEmailsForDropdown(uniqueEmails);
    }
  }, [emailSearchInput, emails]);

  // Apply filters
  useEffect(() => {
    let filtered = [...emails];

    // Apply email search filter
    if (filters.emailSearch) {
      filtered = filtered.filter(email => 
        email.email === filters.emailSearch
      );
    }

    // Apply email search input filter (for typing in search box)
    if (emailSearchInput) {
      filtered = filtered.filter(email => 
        email.email.toLowerCase().includes(emailSearchInput.toLowerCase())
      );
    }

    // Apply signup type filter
    if (filters.signupType !== "all") {
      const isManual = filters.signupType === "manual";
      filtered = filtered.filter(email => email.is_manual === isManual);
    }

    // Apply date sorting
    filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return filters.dateSort === "newest" ? dateB - dateA : dateA - dateB;
    });

    setFilteredEmails(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters, emails, emailSearchInput]);

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      emailSearch: "",
      signupType: "all",
      dateSort: "newest",
    });
    setEmailSearchInput("");
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchEmails(newPage);
    }
  };

  // Calculate pagination for filtered results
  const totalFilteredPages = Math.ceil(filteredEmails.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentEmails = filteredEmails.slice(startIndex, endIndex);

  // Handle file download
  const handleDownload = async () => {
    try {
      const response = await axiosInstance.get("/newsletter/download", {
        responseType: "blob",
      });

      const filename = response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/['"]/g, "");

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename || "emails.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      toast({
        title: "Download Successful",
        description: `File "${filename || "emails.xlsx"}" has been downloaded.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error downloading file:", error);
      toast({
        title: "Error",
        description: "An error occurred while downloading the file.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Box h="100vh" bg="white" overflowY="auto">
      <Box
        flex="1"
        display="flex"
        flexDirection="column"
        maxH="8vh"
        borderBottom="1px"
        borderColor="blackAlpha.200"
        overflowY="hidden"
      >
        <Box
          display="flex"
          alignItems="center"
          bg={colorMode === "dark" ? "gray.700" : "white"}
          width="100%"
          height="100%"
          p={4}
          pt={2}
          pb={2}
          boxSizing="border-box"
          overflowY="hidden"
        >
          <Text
            fontSize="md"
            fontWeight="bold"
            mb={0}
            mt={0}
            color={colorMode === "dark" ? "gray.200" : "blackAlpha.700"}
          >
            Newsletter Subscriptions ({totalEmails})
          </Text>
          <Spacer />
          <Button
            size="sm"
            colorScheme="gray"
            variant="outline"
            onClick={clearFilters}
            mr={2}
          >
            Clear Filters
          </Button>
          <IconButton
            aria-label="Download Excel"
            icon={<Box as={DownloadIcon} boxSize="5" />}
            color="green"
            bg="none"
            _hover="none"
            size="lg"
            onClick={handleDownload}
            isLoading={isLoading}
          />
          <SettingsMenu />
        </Box>
      </Box>



      {/* Table to display emails */}
      <Box p={4} h="calc(100vh - 8vh - 100px)" overflowY="auto">
        <TableContainer w="100%" mx="auto">
          <Table variant="simple" border="1px" borderColor="gray.200" size="sm">
            <Thead>
              <Tr>
                <Th border="1px" borderColor="gray.300" textAlign="center">
                  <HStack spacing={2} justify="center">
                    <Text fontSize="sm" fontWeight="bold" mb={0}>Email Address</Text>
                    <Menu closeOnSelect={false}>
                      <MenuButton as={IconButton} size="xs" variant="ghost" icon={<ChevronDownIcon />} />
                      <MenuList maxH="300px" overflowY="auto">
                        <Box p={2}>
                          <Input
                            size="sm"
                            placeholder="Type to search..."
                            value={emailSearchInput}
                            onChange={(e) => setEmailSearchInput(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Box>
                      </MenuList>
                    </Menu>
                  </HStack>
                </Th>
                <Th border="1px" borderColor="gray.300" textAlign="center">
                  <HStack spacing={2} justify="center">
                    <Text fontSize="sm" fontWeight="bold" mb={0}>Signup Type</Text>
                    <Menu>
                      <MenuButton as={IconButton} size="xs" variant="ghost" icon={<ChevronDownIcon />} />
                      <MenuList>
                        <MenuItem onClick={() => handleFilterChange("signupType", "all")}>
                          All
                        </MenuItem>
                        <MenuItem onClick={() => handleFilterChange("signupType", "manual")}>
                          Manual
                        </MenuItem>
                        <MenuItem onClick={() => handleFilterChange("signupType", "automatic")}>
                          Automatic
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </Th>
                <Th border="1px" borderColor="gray.300" textAlign="center">
                  <HStack spacing={2} justify="center">
                    <Text fontSize="sm" fontWeight="bold" mb={0}>Registration Date</Text>
                    <Menu>
                      <MenuButton as={IconButton} size="xs" variant="ghost" icon={<ChevronDownIcon />} />
                      <MenuList>
                        <MenuItem onClick={() => handleFilterChange("dateSort", "newest")}>
                          Newest First
                        </MenuItem>
                        <MenuItem onClick={() => handleFilterChange("dateSort", "oldest")}>
                          Oldest First
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {currentEmails.map((emailObj, index) => (
                <Tr key={index}>
                  <Td border="1px" borderColor="gray.300" textAlign="center">
                    {emailObj.email}
                  </Td>
                  <Td border="1px" borderColor="gray.300" textAlign="center">
                    <Badge
                      colorScheme={emailObj.is_manual ? "blue" : "green"}
                      variant="subtle"
                    >
                      {emailObj.is_manual ? "Manual" : "Automatic"}
                    </Badge>
                  </Td>
                  <Td border="1px" borderColor="gray.300" textAlign="center">
                    {formatDate(emailObj.date)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex justify="space-between" align="center" mt={4}>
          <Text fontSize="sm" color="gray.600">
            Showing {startIndex + 1} to{" "}
            {Math.min(endIndex, filteredEmails.length)} of{" "}
            {filteredEmails.length} entries
            {filteredEmails.length !== totalEmails && ` (Total: ${totalEmails})`}
          </Text>

          <HStack spacing={2}>
            <Button
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              isDisabled={currentPage === 1}
            >
              Previous
            </Button>

            <Text fontSize="sm" mb={0}>
              Page {currentPage} of {totalFilteredPages || 1}
            </Text>

            <Button
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              isDisabled={currentPage === totalFilteredPages}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      </Box>
    </Box>
  );
};

export default Newsletter;
